import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrders,
  updateOrderStatus,
  selectAllOrders,
  selectPagination,
  selectOrderLoading,
  selectOrderUpdating,
} from "../../../slice/order/orderSlice";

/* ─── Same status config as ViewOrder.jsx, kept in sync ─────── */
const STATUS_CONFIG = {
  pending:      { bg: "#faeeda", color: "#854f0b", border: "#fac775", dot: "#f59e0b", label: "Pending"             },
  accepted:     { bg: "#e6f1fb", color: "#185fa5", border: "#b5d4f4", dot: "#3b82f6", label: "Accepted"            },
  rejected:     { bg: "#fde8e8", color: "#9b1c1c", border: "#f8b4b4", dot: "#ef4444", label: "Rejected"            },
  preparing:    { bg: "#eeedfe", color: "#534ab7", border: "#afa9ec", dot: "#6362e7", label: "Started Preparing"   },
  ready:        { bg: "#eaf3de", color: "#3b6d11", border: "#c0dd97", dot: "#22c55e", label: "Ready for Delivery"  },
  out_delivery: { bg: "#fbeaf0", color: "#993556", border: "#f4c0d1", dot: "#ec4899", label: "Out for Delivery"    },
  delivered:    { bg: "#e1f5ee", color: "#0f6e56", border: "#9fe1cb", dot: "#10b981", label: "Delivered"           },
};

/* ─── Full workflow order, used to know what's still "ahead" of a status ─── */
const STATUS_SEQUENCE = [
  "pending",
  "accepted",
  "rejected",
  "preparing",
  "ready",
  "out_delivery",
  "delivered",
];

/* ─── Admin can only manually push orders through the final delivery leg ─── */
const STATUS_CHANGE_OPTIONS = ["out_delivery", "delivered"];

/* ─── Only the very next status in the sequence is offered — no skipping ahead ─── */
const getAvailableStatusOptions = (currentStatus) => {
  const currentIndex = STATUS_SEQUENCE.indexOf(currentStatus);
  const nextStatus = STATUS_SEQUENCE[currentIndex + 1];
  return STATUS_CHANGE_OPTIONS.filter((s) => s === nextStatus);
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const StatusBadge = ({ status }) => {
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG["pending"];
  return (
    <span style={{
      background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`,
      borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 700,
      display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc.dot, display: "inline-block" }} />
      {sc.label}
    </span>
  );
};

const PaymentBadge = ({ payment }) => {
  const isPaid = payment?.status === "paid";
  return (
    <span style={{
      background: isPaid ? "#e1f5ee" : "#faeeda",
      color:      isPaid ? "#0f6e56" : "#854f0b",
      border:     `1.5px solid ${isPaid ? "#9fe1cb" : "#fac775"}`,
      borderRadius: 20, padding: "4px 12px", fontSize: 11, fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      {payment?.method?.toUpperCase() === "RAZORPAY" ? "Online" : payment?.method?.toUpperCase() || "—"}
      {" · "}{payment?.status || "—"}
    </span>
  );
};

const CustomBadge = () => (
  <span style={{
    background: "#fdf2f8", color: "#be185d", border: "1px solid #fbcfe8",
    borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700,
    display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 8,
  }}>
    🌸 Custom
  </span>
);

/* ─── Dropdown used to pick the next status (replaces the old button group) ─── */
const StatusChangeDropdown = ({ order, disabled, onSelect }) => {
  const availableOptions = getAvailableStatusOptions(order.status);

  if (availableOptions.length === 0) {
    return <span style={{ fontSize: 12, color: "#d1d5db" }}>—</span>;
  }

  const currentSc = STATUS_CONFIG[order.status] || STATUS_CONFIG["pending"];

  return (
    <select
      // Always reset back to a neutral "placeholder" value after a pick,
      // so the dropdown never visually claims the order already changed
      // (the real status only updates once the modal is confirmed).
      value=""
      disabled={disabled}
      onChange={(e) => {
        const newStatus = e.target.value;
        if (newStatus) onSelect(order, newStatus);
        e.target.value = "";
      }}
      style={{
        border: `1.5px solid ${currentSc.border}`,
        background: "#fff",
        color: "#374151",
        borderRadius: 8,
        padding: "6px 10px",
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        minWidth: 150,
      }}
    >
      <option value="" disabled>
        Update status…
      </option>
      {availableOptions.map((s) => (
        <option key={s} value={s}>
          {STATUS_CONFIG[s].label}
        </option>
      ))}
    </select>
  );
};

/* ─── Confirmation modal shown before any status change is applied ─── */
const ConfirmStatusModal = ({ order, newStatus, onConfirm, onCancel, submitting }) => {
  const sc = STATUS_CONFIG[newStatus] || STATUS_CONFIG["pending"];

  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: "24px 24px 20px",
          width: "90%", maxWidth: 380, boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#111827" }}>
          Change order status?
        </h3>

        <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
          Order <strong style={{ color: "#111827" }}>{order.orderId}</strong> will be
          moved to:
        </p>

        <div style={{ margin: "12px 0 20px" }}>
          <StatusBadge status={newStatus} />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8,
              padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#374151",
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            style={{
              border: `1.5px solid ${sc.border}`, background: sc.bg, borderRadius: 8,
              padding: "8px 16px", fontSize: 13, fontWeight: 700, color: sc.color,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? "Updating…" : "Yes, change status"}
          </button>
        </div>
      </div>
    </div>
  );
};

const STATUS_FILTERS = [
  { value: "",             label: "All"                },
  { value: "pending",      label: "Pending"             },
  { value: "accepted",     label: "Accepted"            },
  { value: "rejected",     label: "Rejected"            },
  { value: "preparing",    label: "Preparing"           },
  { value: "ready",        label: "Ready for Delivery"  },
  { value: "out_delivery", label: "Out for Delivery"    },
  { value: "delivered",    label: "Delivered"           },
];

const orderHasCustomItem = (order) =>
  (order.items || []).some((item) => item.isCustom === true);

const OrdersList = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const orders     = useSelector(selectAllOrders);
  const pagination = useSelector(selectPagination);
  const loading    = useSelector(selectOrderLoading);
  const updating   = useSelector(selectOrderUpdating);

  const [page,   setPage]   = useState(1);
  const [status, setStatus] = useState("");
  const limit = 10;

  // ── pending status-change confirmation: { order, newStatus } | null ──
  const [pendingChange, setPendingChange] = useState(null);

  useEffect(() => {
    dispatch(fetchOrders({ page, limit, status }));
  }, [dispatch, page, status]);

  const customCount = useMemo(() => orders.filter(orderHasCustomItem).length, [orders]);

  const totalPages = pagination?.totalPages || 1;

  // ── "Update Status" column shows on Ready for Delivery / Out for Delivery tabs ──
  const showUpdateStatusColumn = status === "ready" || status === "out_delivery";

  const handleStatusSelect = (order, newStatus) => {
    if (!newStatus || newStatus === order.status) return;
    setPendingChange({ order, newStatus });
  };

  const handleConfirmChange = async () => {
    if (!pendingChange) return;
    const { order, newStatus } = pendingChange;

    const result = await dispatch(
      updateOrderStatus({ orderId: order._id, orderStatus: newStatus })
    );

    if (updateOrderStatus.fulfilled.match(result)) {
      setPendingChange(null);
      // refresh current page so the list reflects the change
      dispatch(fetchOrders({ page, limit, status }));
    } else {
      // keep the modal open so the admin can see something went wrong / retry
      setPendingChange(null);
      alert(result.payload || "Failed to update order status");
    }
  };

  const handleCancelChange = () => {
    if (updating) return;
    setPendingChange(null);
  };

  return (
    <div className="container-fluid" style={{ padding: "5px" }}>

      {/* ── Header ── */}
      <div style={{ marginTop: 15, marginBottom: 6 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: 0 }}>
          Order Management
        </h2>
        <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>
          {pagination?.total || orders.length} total orders · {customCount} custom garland
        </div>
      </div>

      {/* ── Status filter pills ── */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "18px 0 16px" }}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => { setStatus(f.value); setPage(1); }}
            style={{
              border: status === f.value ? "1.5px solid #6362e7" : "1.5px solid #e5e7eb",
              background: status === f.value ? "#eeedfe" : "#fff",
              color: status === f.value ? "#6362e7" : "#374151",
              borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Table Card ── */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div className="card-body" style={{ padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Order ID", "Customer", "Items", "Total", "Status", ...(showUpdateStatusColumn ? ["Update Status"] : []), "Payment", "Placed On", ""].map((h) => (
                  <th key={h} style={{
                    padding: "12px 16px", textAlign: h === "" ? "right" : "left",
                    fontSize: 11, fontWeight: 700, color: "#6b7280",
                    textTransform: "uppercase", letterSpacing: "0.05em",
                    whiteSpace: "nowrap",
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={showUpdateStatusColumn ? 9 : 8} style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                    Loading orders…
                  </td>
                </tr>
              )}

              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan={showUpdateStatusColumn ? 9 : 8} style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                    No orders found.
                  </td>
                </tr>
              )}

              {!loading && orders.map((order) => {
                const addr = order.shippingAddress || {};
                const customerName = addr.name || addr.fullName || "—";
                const isCustomOrder = orderHasCustomItem(order);

                return (
                  <React.Fragment key={order._id}>
                    <tr
                      style={{ borderTop: "1px solid #f3f4f6" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td
                       
                        style={{ padding: "14px 16px", fontWeight: 700, fontSize: 13, color: "#111827", cursor: "pointer" }}
                      >
                        {order.orderId}
                        {isCustomOrder && <CustomBadge />}
                      </td>
                      <td
                        onClick={() => navigate(`/admin/order/view/${order._id}`)}
                        style={{ padding: "14px 16px", fontSize: 13, color: "#374151", cursor: "pointer" }}
                      >
                        {customerName}
                      </td>
                      <td
                        onClick={() => navigate(`/admin/order/view/${order._id}`)}
                        style={{ padding: "14px 16px", fontSize: 13, color: "#6b7280", cursor: "pointer" }}
                      >
                        {(order.items || []).length} item{(order.items || []).length !== 1 ? "s" : ""}
                      </td>
                      <td
                        onClick={() => navigate(`/admin/order/view/${order._id}`)}
                        style={{ padding: "14px 16px", fontWeight: 700, fontSize: 13, color: "#111827", cursor: "pointer" }}
                      >
                        ${(order.totalAmount || 0).toLocaleString("en-IN")}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <StatusBadge status={order.status} />
                      </td>

                      {/* ── STATUS CHANGE DROPDOWN (Ready / Out for Delivery tabs only) ── */}
                      {showUpdateStatusColumn && (
                        <td style={{ padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                          <StatusChangeDropdown
                            order={order}
                            disabled={updating}
                            onSelect={handleStatusSelect}
                          />
                        </td>
                      )}

                      <td style={{ padding: "14px 16px" }}>
                        {/* <PaymentBadge payment={order.payment} /> */}
                        <p
                          style={{
                            background: "rgb(225, 245, 238)",
                            color: "rgb(15, 110, 86)",
                            border: "1.5px solid rgb(159, 225, 203)",
                            borderRadius: "20px",
                            padding: "4px 12px",
                            fontSize: "11px",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Online - success
                        </p>
                      </td>
                      <td
                       
                        style={{ padding: "14px 16px", fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap", cursor: "pointer" }}
                      >
                        {formatDateTime(order.createdAt)}
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/orders/details/${order._id}`); }}
                          style={{
                            border: "1px solid #e5e7eb", background: "#f9fafb",
                            borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600,
                            color: "#374151", cursor: "pointer",
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>

                    {/* ── Customization strip (shown under order row if any item is custom) ── */}
                    {isCustomOrder && (order.items || [])
                      .filter((item) => item.isCustom)
                      .map((item, idx) => (
                        <tr key={`${order._id}-custom-${idx}`}>
                          <td colSpan={showUpdateStatusColumn ? 9 : 8} style={{ padding: "0 16px 12px" }}>
                            <div style={{
                              background: "#fffbea", border: "1px solid #fde68a",
                              borderRadius: 10, padding: "10px 14px",
                              display: "flex", alignItems: "center", gap: 16,
                              fontSize: 12, color: "#92400e", flexWrap: "wrap",
                            }}>
                              <span style={{ fontWeight: 700 }}>🌸 Customization:</span>
                              {item.kunjam_count > 0 && (
                                <span>💐 Kunjam: {item.kunjam_count} Flowers</span>
                              )}
                              {item.flowers && (
                                <span>🌺 {item.flowers}</span>
                              )}
                              <span style={{ fontWeight: 800, textTransform: "uppercase", marginLeft: "auto" }}>
                                {item.name}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && orders.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 16px", borderTop: "1px solid #f3f4f6",
          }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>
              Page {page} of {totalPages} · {pagination?.total || 0} total orders
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8,
                  padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#374151",
                  cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8,
                  padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#374151",
                  cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Confirmation modal ── */}
      {pendingChange && (
        <ConfirmStatusModal
          order={pendingChange.order}
          newStatus={pendingChange.newStatus}
          onConfirm={handleConfirmChange}
          onCancel={handleCancelChange}
          submitting={updating}
        />
      )}
    </div>
  );
};

export default OrdersList;